import React, { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  CircularProgress,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { commonContainerWrapper } from "../Common/styles/flex";
import { NoDataContainer } from "../Common/styles/table";
import AddGlobalTagModal from "../ParticipantRecords/ParticipantTags/AddGlobalTagsModal";
import http from "../../utils/http";
import { errorToastMessage } from "../../utils/toast";

type Tag = {
  id: string;
  name: string;
  studyId: string | null;
  category?: string;
  slug?: string | null;
  isEditable?: boolean;
};

const GlobalTagsCard = ({
  tagsList,
  refreshPage,
  canEdit = false,
}: {
  tagsList: Tag[];
  refreshPage: () => void;
  canEdit: boolean;
}) => {
  const { participantId } = useParams<{ participantId: string }>();
  const [showModal, setShowModal] = useState(false);
  const [savingSlug, setSavingSlug] = useState<string | null>(null);

  const exclusiveTags = tagsList.filter(
    (t) => t.category === "system_exclusive"
  );
  const multiTags = tagsList.filter((t) => t.category === "system_multi");
  const userTags = tagsList.filter(
    (t) => !t.category || t.category === "user"
  );

  const isExclusiveTagAssigned = (slug: string) =>
    exclusiveTags.some((t) => t.slug === slug);

  const handleSystemTagToggle = async (slug: string, checked: boolean) => {
    if (!participantId) return;
    try {
      setSavingSlug(slug);
      await http.patch(
        `/participant-tags/registry/${participantId}/system-tags`,
        { slug, assigned: checked }
      );
      refreshPage();
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setSavingSlug(null);
    }
  };

  const hasSystemContent = exclusiveTags.length > 0 || multiTags.length > 0;
  const hasUserContent = userTags.length > 0;

  return (
    <Box sx={{ ...commonContainerWrapper, mt: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="h2" color="text.primary">
          Tags
        </Typography>
        {canEdit && (
          <Box sx={{ ml: "auto" }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setShowModal(true)}
            >
              Add Tag
            </Button>
          </Box>
        )}
      </Box>

      {/* System exclusive section (Do Not Contact) */}
      {exclusiveTags.length > 0 || canEdit ? (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            System Status
          </Typography>
          <FormGroup>
            {/* Always render the DNC checkbox so it can be toggled on */}
            <FormControlLabel
              control={
                savingSlug === "do_not_contact" ? (
                  <CircularProgress size={20} sx={{ mx: 1.25 }} />
                ) : (
                  <Checkbox
                    checked={isExclusiveTagAssigned("do_not_contact")}
                    disabled={!canEdit || savingSlug !== null}
                    onChange={(e) =>
                      handleSystemTagToggle("do_not_contact", e.target.checked)
                    }
                  />
                )
              }
              label="Do Not Contact"
            />
          </FormGroup>
        </Box>
      ) : null}

      {/* System multi section */}
      {multiTags.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              System Flags
            </Typography>
            <FormGroup>
              {multiTags.map((tag) => (
                <FormControlLabel
                  key={tag.id}
                  control={
                    savingSlug === tag.slug ? (
                      <CircularProgress size={20} sx={{ mx: 1.25 }} />
                    ) : (
                      <Checkbox
                        checked
                        disabled={!canEdit || savingSlug !== null}
                        onChange={(e) =>
                          tag.slug &&
                          handleSystemTagToggle(tag.slug, e.target.checked)
                        }
                      />
                    )
                  }
                  label={tag.name}
                />
              ))}
            </FormGroup>
          </Box>
        </>
      )}

      {/* User tags section */}
      {(hasSystemContent || multiTags.length > 0) && <Divider sx={{ my: 1.5 }} />}
      <Box>
        {(userTags.length > 0 || exclusiveTags.length > 0 || multiTags.length > 0) && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Tags
          </Typography>
        )}
        {(userTags.length > 0 || exclusiveTags.length > 0 || multiTags.length > 0) ? (
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {[...exclusiveTags, ...multiTags, ...userTags].map((tag) => (
              <Chip
                sx={{
                  backgroundColor: "#F6F1F8",
                  paddingX: "10px",
                  paddingY: "2px",
                  borderRadius: "6px",
                  fontSize: 14,
                  fontWeight: 600,
                  color: "primary.main",
                  m: 0.5,
                }}
                key={tag.id}
                label={tag.name}
              />
            ))}
          </Box>
        ) : (
          exclusiveTags.length === 0 && multiTags.length === 0 && !hasSystemContent && (
            <NoDataContainer>
              <Typography variant="body1" color="gray">
                No Data
              </Typography>
            </NoDataContainer>
          )
        )}
      </Box>

      {showModal && (
        <AddGlobalTagModal
          showModal={showModal}
          closeModal={() => setShowModal(false)}
          refreshPage={refreshPage}
          existingTags={userTags}
        />
      )}
    </Box>
  );
};

export default GlobalTagsCard;
